import React, { useEffect } from 'react'

import { yupResolver } from '@hookform/resolvers/yup'
import { Controller, useForm, SubmitHandler } from 'react-hook-form'
import * as Yup from 'yup'

import { DeleteZoneRequestDTO } from '@/core/api/dtos/request/zone/delete'
import { Zone } from '@/core/api/entities/zone/zone'
import { useTranslation } from '@/core/i18n/useTranslation'
import { Button } from '@/shared/components/atoms/Button'
import { ErrorMessage } from '@/shared/components/atoms/ErrorMessage'
import { Input } from '@/shared/components/atoms/Input'
import { Label } from '@/shared/components/atoms/Label/Label'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/shared/components/atoms/Select'
import {
	Dialog,
	DialogContent,
	DialogTitle,
} from '@/shared/components/molecules/Dialog/dialog'

interface DeleteZoneModalProps {
	visible: boolean
	zone: Zone | null
	onClose: () => void
	onConfirm: (data: DeleteZoneModalFormData) => void
	isDeleting: boolean
	onPage: 'subscriptions' | 'zones'
}

export interface DeleteZoneModalFormData extends DeleteZoneRequestDTO {
	/** Password confirmation used for subscription downgrade flow (NOT sent to /my/zones/{id}) */
	password: string
}

const DeleteZoneModal: React.FC<DeleteZoneModalProps> = ({
	visible,
	zone,
	onClose,
	onConfirm,
	isDeleting,
	onPage,
}) => {
	const { t } = useTranslation('common')

	// Default values for the form
	const defaultValues: DeleteZoneModalFormData = {
		reason: 'STORE_CLOSED',
		password: '',
	}

	// Create validation schema using Yup
	// Password is required only for the subscription downgrade flow.
	const validationSchema = Yup.object().shape({
		reason: Yup.string()
			.required(t('validation.required'))
			.oneOf(
				['STORE_CLOSED', 'TECHNICAL_ISSUES', 'MUSICAL_DECISION'],
				t('validation.invalidOption')
			)
			.default(defaultValues.reason),
		password:
			onPage === 'subscriptions'
				? Yup.string()
						.required(t('validation.required'))
						.min(6, t('validation.minLength', { min: 6 }))
						.default(defaultValues.password)
				: Yup.string().default(defaultValues.password),
	})

	const {
		control,
		handleSubmit,
		formState: { errors: _errors },
		reset,
	} = useForm<DeleteZoneModalFormData>({
		defaultValues,
		resolver: yupResolver(validationSchema),
	})

	// Reset form when modal closes
	useEffect(() => {
		if (!visible) {
			reset(defaultValues)
		}
	}, [visible, reset])

	const onSubmit: SubmitHandler<DeleteZoneModalFormData> = (data) => {
		onConfirm(data)
	}

	const reasonOptions = [
		{
			value: 'STORE_CLOSED',
			label: t('entities.zone.delete.form.reason.options.STORE_CLOSED'),
		},
		{
			value: 'TECHNICAL_ISSUES',
			label: t('entities.zone.delete.form.reason.options.TECHNICAL_ISSUES'),
		},
		{
			value: 'MUSICAL_DECISION',
			label: t('entities.zone.delete.form.reason.options.MUSICAL_DECISION'),
		},
	]

	return (
		<Dialog open={visible} onOpenChange={onClose}>
			<DialogContent
				className="sm:max-w-[500px] max-h-[90vh] p-0 gap-0 overflow-hidden flex flex-col"
				showCloseButton={false}
			>
				{/* Header */}
				<div className="px-6 py-6 border-b">
					<DialogTitle className="text-2xl">
						{t('entities.zone.delete.title')}
					</DialogTitle>
				</div>

				{/* Content */}
				<form
					id="delete-zone-form"
					onSubmit={handleSubmit(onSubmit)}
					className="flex flex-col flex-1 min-h-0"
				>
					<div className="px-6 py-6 space-y-6 overflow-y-auto flex-1">
						{/* Zone Description */}
						<div className="text-center">
							<p className="text-lg font-semibold">
								{t('entities.zone.delete.description', {
									name: zone?.name,
								})}
							</p>
						</div>

						{/* Warning Note */}
						<div className="text-center text-sm text-muted-foreground">
							{/* if on subscriptions page, show this warning*/}
							{onPage === 'subscriptions' && (
								<p className="text-sm text-muted-foreground">
									{t('account.subscription.delete_zone_warning')}
								</p>
							)}

							{/* if not on subscriptions page, show this warning*/}
							{onPage === 'zones' && (
								<p className="text-sm text-muted-foreground">
									{t('entities.zone.delete.warning')}{' '}
									<a
										href="mailto:success@brandtrack.fm"
										className="text-primary hover:underline"
									>
										success@brandtrack.fm
									</a>
								</p>
							)}
						</div>

						{/* Form Fields */}
						<div className="space-y-6 max-w-md mx-auto w-full">
							{/* Reason */}
							<div>
								<Label className="mb-2 block">
									{t('entities.zone.delete.form.reason.label')}*
								</Label>
								<Controller
									name="reason"
									control={control}
									render={({ field, fieldState: { error } }) => (
										<div>
											<Select
												value={field.value}
												onValueChange={field.onChange}
												disabled={isDeleting}
											>
												<SelectTrigger className="h-10 w-full text-sm">
													<SelectValue
														placeholder={t(
															'entities.zone.delete.form.reason.placeholder'
														)}
													/>
												</SelectTrigger>
												<SelectContent>
													{reasonOptions.map((option) => (
														<SelectItem key={option.value} value={option.value}>
															{option.label}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
											{error && <ErrorMessage error={error.message ?? ''} />}
										</div>
									)}
								/>
							</div>

							{/* Password (subscription downgrade flow only) */}
							{onPage === 'subscriptions' && (
								<div>
									<Label className="mb-2 block">
										{t('entities.zone.delete.form.password.label')}*
									</Label>
									<Controller
										name="password"
										control={control}
										render={({ field, fieldState: { error } }) => (
											<div>
												<Input
													{...field}
													type="password"
													placeholder={t(
														'entities.zone.delete.form.password.placeholder'
													)}
													disabled={isDeleting}
													className="h-10 text-sm"
												/>
												{error && <ErrorMessage error={error.message ?? ''} />}
											</div>
										)}
									/>
								</div>
							)}
						</div>
					</div>

					{/* Footer */}
					<div className="px-6 py-6 border-t flex justify-end gap-3">
						<Button
							type="button"
							variant="outline"
							onClick={onClose}
							disabled={isDeleting}
							className="rounded-full px-6"
						>
							{t('actions.cancel')}
						</Button>
						<Button
							type="submit"
							variant="destructive"
							disabled={isDeleting}
							className="rounded-full px-6"
						>
							{isDeleting ? t('actions.loading') : t('actions.delete')}
						</Button>
					</div>
				</form>
			</DialogContent>
		</Dialog>
	)
}

export default DeleteZoneModal
